import { Category } from 'types/entity/category';

// Env-overridable so a staging site can point PDPs at a staging builder without a code change,
// mirroring `customQuoteLink` (product-list/context/constants.ts). Documented in .nextpublicrc.
const BUILDER_BASE = process.env.NEXT_PUBLIC_TREAD_BUILDER_BASE_URL || 'https://treads.viewrail.com/build';

/**
 * Resolves the Tread Designer builder URL for a stair-tread PDP, or `null` when the product
 * isn't a tread we have a builder for (so the callout simply hides — a safe default).
 *
 * The site categorises treads by thickness/type (commercetools `categoryKey`), but the builder
 * is organised by construction family, so two categories host more than one family and are
 * disambiguated by the product name:
 *   - `thick-stair-treads`        → Signature vs Butcher Block (name contains "Butcher Block")
 *   - `stair-risers-and-other-treads` → Riser vs S4S vs Solid (name contains "Riser"/"S4S")
 */
export const getTreadBuilderUrl = (categories: Category[] | undefined, name: string | undefined): string | null => {
  const categoryKeys = new Set((categories ?? []).map((category) => category.categoryKey).filter(Boolean) as string[]);
  const productName = (name ?? '').toLowerCase();

  let builder: string | null = null;

  // First matching branch wins. In practice a tread belongs to a single leaf family, so overlaps
  // (e.g. both thick-stair-treads and replacement-treads) shouldn't occur; if one ever does, the
  // order below is the tie-breaker.
  if (categoryKeys.has('thick-stair-treads')) {
    // Tolerate the "Butcher Block"/"Butcherblock" spellings; everything else in the category is Signature.
    builder = /butcher\s*block/.test(productName) ? 'custom-butcher-block-thick-tread' : 'custom-signature-thick-tread';
  } else if (categoryKeys.has('thick-landing-platforms')) {
    // Thick platforms split by family exactly as thick treads do, and they are their own builders —
    // not the solid-platform one, which belongs to the 1" family below. The PDPs for this category
    // already carry an authored builder link, so sending the callout somewhere else put two links
    // to different builders side by side on the same page.
    builder = /butcher\s*block/.test(productName)
      ? 'custom-butcher-block-thick-platform'
      : 'custom-signature-thick-platform';
  } else if (categoryKeys.has('1-landing-platforms')) {
    builder = 'custom-solid-platform';
  } else if (categoryKeys.has('1-stair-treads')) {
    builder = 'custom-solid-tread';
  } else if (categoryKeys.has('replacement-treads') || categoryKeys.has('replacement-landing-platforms')) {
    builder = 'custom-replacement-tread';
  } else if (categoryKeys.has('stair-risers-and-other-treads')) {
    // This category mixes families: risers and S4S boards are named as such; the remaining
    // "other treads" (e.g. landing treads — solid boards) map to the solid-tread builder.
    builder = productName.includes('riser')
      ? 'custom-riser'
      : productName.includes('s4s')
        ? 'custom-s4s-board'
        : 'custom-solid-tread';
  }

  return builder ? `${BUILDER_BASE}/${builder}` : null;
};
